import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { HostModeService } from '@shared/services/hostMode.service';
import { ToastrService } from '@shared/services/toastr.service';
import { Site } from '@shared/models/site';
import { OrganizationSiteService, RewriteRule } from '@shared/services/organization-site.service';
import _ from 'lodash';
import { MakeOptional } from '@shared/utils/type-utils';
import { Audit } from '@shared/models/audit';
import { ConfirmService } from '@shared/services/confirm.service';
import { HelpComponent } from '@shared/components/help/help.component';
import { NgClass, NgIf, NgStyle } from '@angular/common';
import { SwitchComponent } from '@shared/components/switch/switch.component';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { LabelComponent } from '@shared/components/label/label.component';
import { DateFormatPipe } from '@shared/pipe/filter.pipe';

@Component({
  selector: 'app-rewrite-rules',
  templateUrl: './rewrite-rules.component.html',
  styleUrls: ['./rewrite-rules.component.scss', '../add-site.component.scss', '../activable-and-ordered.scss'],
  imports: [
    HelpComponent,
    NgIf,
    CdkDropList,
    CdkDrag,
    NgClass,
    SwitchComponent,
    FormsModule,
    NgbTooltip,
    LabelComponent,
    NgStyle,
    DateFormatPipe,
    TranslatePipe,
  ],
})
export class RewriteRulesComponent implements OnInit {
  @Input({ required: true }) site: Site;

  @ViewChild('rewriteRuleSourceElement') rewriteRuleSourceElement: ElementRef;
  @ViewChild('rewriteTest') rewriteTest: ElementRef;

  rewriteRules: RewriteRuleView[] = [];
  disabled = true;
  tmpRewriteRule: any = {};
  editedIndex: number;
  rewriteRulesInProgress = false;
  testURL: string;

  constructor(
    private organizationSiteService: OrganizationSiteService,
    private translate: TranslateService,
    public hostModeService: HostModeService,
    private toastr: ToastrService,
    private confirmService: ConfirmService,
  ) {}

  ngOnInit(): void {
    this.disabled = !this.hostModeService.isAdvanced(this.site.mode);
    this.getRewriteRules();
  }

  getRewriteRules() {
    this.organizationSiteService.getRewriteRules(this.site.domainName).subscribe({
      next: (res) => {
        this.rewriteRules = _(res)
          .map((rule) => ({
            ...rule,
            rewriteSource: removeSourcePrefix(rule.rewriteSource),
            rewriteDestination: removeDestPrefix(rule.rewriteDestination),
            isNew: false,
            isEditing: false,
          }))
          .value();
      },
      error: (error: any) => {
        this.toastr.error('Failed to load rewrite rules');
        console.error('Error : ', error);
      },
    });
  }

  startRewriteEditing(index: number) {
    this.editedIndex = index;
    const rule = this.rewriteRules[index];
    this.tmpRewriteRule = _.cloneDeep(rule);
    rule.isEditing = true;
  }

  validateRewriteEditing(rule: RewriteRuleView) {
    if (rule.rewriteSource && rule.rewriteDestination) {
      rule.isEditing = false;

      const { isNew, isEditing, createdAt, updatedAt, ...data } = rule;

      if (rule.rewriteDestination[0] !== '/') {
        rule.rewriteDestination = '/' + rule.rewriteDestination;
      }

      if (rule.rewriteDestination[0] !== '/') {
        rule.rewriteSource = '/' + rule.rewriteSource;
      }

      if (data.id) {
        this.updateRewriteRule(data as RewriteRule);
      } else {
        this.createRule(data);
      }
    }
  }

  createRule(data: Omit<RewriteRule, 'id'>) {
    this.rewriteRulesInProgress = true;
    this.organizationSiteService.createRewriteRule(this.site.domainName, conformRule(data)).subscribe({
      next: (_) => {
        this.toastr.success();
        this.rewriteRulesInProgress = false;
        this.getRewriteRules();
      },
      error: (error: any) => {
        this.rewriteRulesInProgress = false;
        this.toastr.error('Failed to save rewrite rule');
        console.error('Error : ', error);
      },
    });
  }

  updateRewriteRule(rule: RewriteRule) {
    this.rewriteRulesInProgress = true;
    this.organizationSiteService.updateRewriteRule(this.site.domainName, conformRule(rule)).subscribe({
      next: (_) => {
        this.toastr.success();
        this.rewriteRulesInProgress = false;
        this.getRewriteRules();
      },
      error: (error: any) => {
        this.toastr.error('Failed to save rewrite rule');
        console.error('Error : ', error);
      },
    });
  }

  cancelRewriteEditing(rule) {
    rule.editing = false;
    this.rewriteRules[this.editedIndex] = this.tmpRewriteRule;
  }

  addRewriteRule() {
    if (this.disabled) {
      return this.hostModeService.promptSubscribeToMode('SiteModeNotEnabled.advanced');
    }

    this.rewriteRules.push({
      isNew: true, // front only
      isEditing: true, // front only
      active: true,
      rewriteSource: '',
      rewriteDestination: '',
      comment: '',
    });
    setTimeout(() => this.rewriteRuleSourceElement.nativeElement.focus(), 1);
  }

  deleteRewriteRule(rule: RewriteRuleView) {
    if (rule.isNew) {
      this.rewriteRules = this.rewriteRules.filter((r) => r != rule);
    } else {
      return this.confirmService.confirm(
        {
          html: this.translate.instant(
            rule.id ? 'VoulezVousAbandonnerCetteNouvelleRegle' : 'VoulezVousSupprimerCetteRegle',
          ),
        },
        () => {
          if (rule.id) {
            this.rewriteRulesInProgress = true;
            this.organizationSiteService.deleteRewriteRule(this.site.domainName, rule.id).subscribe({
              next: (_) => {
                this.toastr.success();
                this.getRewriteRules();
                this.rewriteRulesInProgress = false;
              },
              error: (error: any) => {
                this.toastr.error('Failed to delete rewrite rule');
                console.error('Error : ', error);
              },
            });
          } else {
            this.rewriteRules.splice(
              this.rewriteRules.findIndex((r) => r.isNew),
              1,
            );
          }
        },
      );
    }
  }

  isCreatingRewriteRule() {
    return this.rewriteRules.find((rule) => rule.isNew);
  }

  isEditingRewriteRule() {
    return this.rewriteRules.some((rule) => rule.isEditing);
  }

  dropRewrite(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.rewriteRules, event.previousIndex, event.currentIndex);
    this.rewriteRulesInProgress = true;
    this.organizationSiteService
      .patchSite(this.site.domainName, { rewriteRules: this.rewriteRules.map(conformRule) })
      .subscribe({
        next: (updatedSite) => {
          this.toastr.success('SiteModifieSucc');
          this.rewriteRulesInProgress = false;
        },
        error: (err) => {
          this.rewriteRulesInProgress = false;
          moveItemInArray(this.rewriteRules, event.currentIndex, event.previousIndex);
        },
      });
  }

  computeRegExp(rule: { rewriteSource: string; rewriteDestination: string }) {
    let value = this.rewriteTest.nativeElement.value;
    let queryString = '';

    if (value.includes('?')) {
      queryString = value.split('?')[1];
      value = value.split('?')[0];
    }

    const sourceURL = new RegExp(`^${rule.rewriteSource}`, 'i');

    let tmp = rule.rewriteDestination.replace(/\${(\d+)}/g, '$$$1');

    this.testURL = `${value.replace(sourceURL, tmp)}${queryString ? `?${queryString}` : ''}`;

    let matches = value.match(sourceURL);

    if (!matches && value == '') {
      this.testURL = undefined;
    } else if (!matches) {
      this.testURL = this.translate.instant('NoMatch');
    }
  }

  isValidRewriteRegExp(regExp) {
    let isValid = true;
    try {
      new RegExp(regExp);
    } catch (e) {
      isValid = false;
    }

    if (regExp.includes('?')) {
      isValid = false;
    }

    return isValid;
  }

  onPasteSource(event: ClipboardEvent, rule: RewriteRuleView) {
    event.preventDefault();

    const inputElt = event.target as HTMLInputElement;
    const { selectionStart: start, selectionEnd: end, value: current } = inputElt;

    rule.rewriteSource = removeSourcePrefix(
      current.slice(0, start) + event.clipboardData.getData('text/plain') + current.slice(end),
    );
  }

  onPasteDestination(event: ClipboardEvent, rule: RewriteRuleView) {
    event.preventDefault();

    const inputElt = event.target as HTMLInputElement;
    const { selectionStart: start, selectionEnd: end, value: current } = inputElt;

    rule.rewriteDestination = removeDestPrefix(
      current.slice(0, start) + event.clipboardData.getData('text/plain') + current.slice(end),
    );
  }
}

function conformRule<T extends Omit<RewriteRule, 'id'>>(rule: T): T {
  return {
    ...rule,
    rewriteSource: conformSource(rule.rewriteSource),
    rewriteDestination: confirmDestination(rule.rewriteDestination),
  };
}

function removeSourcePrefix(regex: string) {
  return regex.replace(/^\^?\/*/, '');
}

function conformSource(regex: string) {
  if (regex.startsWith('^/')) return regex;
  else return '^/' + removeSourcePrefix(regex);
}

function removeDestPrefix(path: string) {
  return path.replace(/\/*/, '');
}

function confirmDestination(path: string) {
  if (path.startsWith('/')) return path;
  else return '/' + removeDestPrefix(path);
}

type RewriteRuleView = MakeOptional<RewriteRule, 'id'> & Partial<Audit> & { isNew: boolean; isEditing: boolean };
